/**
 * Deep Link Context
 *
 * Provides context for coordinating deep link navigation between
 * App.tsx (URL parsing) and Dashboard (navigation execution).
 *
 * Flow:
 * 1. App.tsx parses URL on mount → sets pendingDeepLink
 * 2. Dashboard reads pendingDeepLink → executes navigation
 * 3. Dashboard clears pendingDeepLink after navigation
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import { DeepLink } from '../utils/deep-linking';

interface DeepLinkContextValue {
  pendingDeepLink: DeepLink | null;
  setPendingDeepLink: (deepLink: DeepLink | null) => void;
}

const DeepLinkContext = createContext<DeepLinkContextValue | undefined>(
  undefined
);

interface DeepLinkProviderProps {
  children: ReactNode;
}

export function DeepLinkProvider({ children }: DeepLinkProviderProps) {
  const [pendingDeepLink, setPendingDeepLink] = useState<DeepLink | null>(null);

  return (
    <DeepLinkContext.Provider value={{ pendingDeepLink, setPendingDeepLink }}>
      {children}
    </DeepLinkContext.Provider>
  );
}

/**
 * Hook to access deep link context
 * Use in App.tsx to set pending deep link
 * Use in Dashboard to consume and clear pending deep link
 */
// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export function useDeepLink(): DeepLinkContextValue {
  const context = useContext(DeepLinkContext);
  if (!context) {
    throw new Error('useDeepLink must be used within DeepLinkProvider');
  }
  return context;
}
